import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import AnimeCardComponent from './animecard/AnimeCardComponent';
import VirtualHList from './virtual/VirtualHList';
import SectionHeader from './layout/SectionHeader';
import arrowIcon from '../assets/ic_arrow_right.svg';

/**
 * Horizontal row of anime cards with a section heading, optional "see all"
 * fade overlay (a header button on mobile) and desktop scroll arrows.
 */
const AnimeRow = ({ title, eyebrow, icon, items = [], to, onAction, renderItem }) => {
    const listRef = useRef(null);
    const [arrows, setArrows] = useState({ left: false, right: false });

    const updateArrows = useCallback(() => {
        const el = listRef.current;
        if (!el) return;
        const { scrollLeft, scrollWidth, clientWidth } = el;
        const next = { left: scrollLeft > 0, right: scrollLeft + clientWidth < scrollWidth - 1 };
        setArrows((prev) => (prev.left === next.left && prev.right === next.right ? prev : next));
    }, []);

    useEffect(() => {
        updateArrows();
        const el = listRef.current;
        if (!el) return undefined;
        el.addEventListener('scroll', updateArrows, { passive: true });
        window.addEventListener('resize', updateArrows);
        return () => {
            el.removeEventListener('scroll', updateArrows);
            window.removeEventListener('resize', updateArrows);
        };
    }, [items, updateArrows]);

    // Native smooth scrolling: the row already has scroll-behavior: smooth, so a JS tween
    // that sets scrollLeft every frame fights it and stalls the start by ~0.5s.
    const scroll = (direction) => {
        const el = listRef.current;
        if (!el) return;
        const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        el.scrollBy({ left: direction * Math.round(el.clientWidth * 0.8), behavior: reduceMotion ? 'auto' : 'smooth' });
    };

    return (
        <section className="container-wrapper anime-row">
            {/* On desktop "see all" is the fade overlay at the row's end; the header button is mobile-only. */}
            <SectionHeader
                title={title}
                eyebrow={eyebrow}
                icon={icon}
                to={to}
                onAction={onAction}
                className={to || onAction ? 'section-header--mobile-action' : ''}
            />
            <div className="anime-line-container-padding">
                {arrows.left ? (
                    <button type="button" className="scroll-button left" onClick={() => scroll(-1)} aria-label="Прокрутить назад">
                        <img src={arrowIcon} alt="" />
                    </button>
                ) : null}
                <VirtualHList
                    containerRef={listRef}
                    items={items}
                    renderItem={renderItem || ((anime) => (
                        <AnimeCardComponent key={anime.id} anime={anime} isInteractive={true} />
                    ))}
                />
                {to ? (
                    <Link to={to} className="view-all-button">Смотреть все</Link>
                ) : null}
                {!to && onAction ? (
                    <button type="button" className="view-all-button" onClick={onAction}>Смотреть все</button>
                ) : null}
                {arrows.right && !to && !onAction ? (
                    <button type="button" className="scroll-button right" onClick={() => scroll(1)} aria-label="Прокрутить вперёд">
                        <img src={arrowIcon} alt="" />
                    </button>
                ) : null}
            </div>
        </section>
    );
};

export default AnimeRow;
